import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { toJS } from 'mobx'
import Button from '@components/Button'
import FormLoader from '@components/FormLoader'
import PropTypes from 'prop-types'
import FormError from '@components/FormError'
import { Formik, Form, Field, FieldArray } from 'formik'
import Input from '@components/Form/Input'
import RepeatableAction from '@components/Form/RepeatableAction'
import yup from 'yup'
import { invoiceApi } from '@atheneum/api'
import { moneyValidate } from '@atheneum/api/dist/lib'

class InvoiceItemsForm extends Component {
  static propTypes = {
    modalStore: PropTypes.object,
    staticData: PropTypes.object,
    title: PropTypes.string,
    invoice: PropTypes.object,
    growlStore: PropTypes.object,
    invoiceStore: PropTypes.object,
    contract: PropTypes.object,
    deliverableStore: PropTypes.object
  }

  static defaultProps = {
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: true
  }

  state = {
    error: null,
    loading: false
  }

  submitForm = async (values, actions) => {
    const { invoice, modalStore, growlStore } = this.props

    const isInvoiceCreation = !invoice.id

    const invoiceItems = values.invoiceItems.map(item => {
      const netAmout = parseFloat(item.netAmount) || 0
      return {
        ...item,
        netAmount: netAmout
      }
    })

    try {
      this.setState({
        error: null,
        loading: true
      })

      if (isInvoiceCreation) {
        await this.createInvoiceItems(invoice, invoiceItems)
      } else {
        await this.updateInvoiceItems(invoice, invoiceItems)
      }

      modalStore.close()
    } catch (error) {
      growlStore.showError(error)
      actions.setSubmitting(false)
      this.setState({
        error,
        loading: false
      })
    }
  }

  createInvoiceItems = async (invoice, invoiceItems) => {
    const { invoiceStore, deliverableStore, contract, growlStore } = this.props

    const invoiceData = {
      ...invoice,
      invoiceItems
    }

    const createdInvoice = await invoiceApi.createInvoice(invoiceData)

    growlStore.showSuccess(`Created invoice ${createdInvoice.name}`)

    invoiceStore.getListByContract(contract.id)
    deliverableStore.getByContract(contract.id)
  }

  updateInvoiceItems = async (invoice, invoiceItems) => {
    const { invoiceStore, growlStore } = this.props

    await invoiceApi.updateInvoiceItems(invoice.id, invoiceItems)

    growlStore.showSuccess('Updated invoice data.')

    invoiceStore.get(invoice.id)
  }

  render () {
    const { title, modalStore, staticData, invoice } = this.props
    const currencyNames = staticData.names.currencies
    const currencyName = (invoice && currencyNames[invoice.currencyId]) || 'N/A'

    return (
      <Formik
        initialValues={initializeForm(this.props)}
        validationSchema={getValidationSchema()}
        onSubmit={this.submitForm}
        render={form => {
          return (
            <div className='expert-form invoice-form'>
              <Form onSubmit={form.handleSubmit}>
                <FormLoader loading={form.isSubmitting} />
                <h1 className='expert-form__title'>{title}</h1>
                <FormError error={this.error} />

                <InvoiceItems
                  form={form}
                  label={`Description / Net amount (${currencyName})`}
                  name='invoiceItems'
                />

                <div className='expert-form__section p-b-0'>
                  <Button primary submit disabled={form.isSubmitting}>
                    Save
                  </Button>
                  <Button onClick={modalStore.close}>Cancel</Button>
                </div>
              </Form>
            </div>
          )
        }}
      />
    )
  }
}

const InvoiceItems = ({ form, label, name }) => {
  const invoiceItems = form.values.invoiceItems

  return (
    <div className='expert-form__input-group m-b-xl'>
      <label className='expert-form__label'>{label}</label>
      <div className='expert-form__input-wrapper'>
        <FieldArray
          name={name}
          render={arrayHelpers => (
            <div className='expert-form__repeatable-input-wrapper'>
              {invoiceItems.map((invoiceItem, index) => (
                <div
                  className='expert-form__repeatable-input expert-form__repeatable-input--multiple'
                  key={index}
                >
                  <Field
                    name={`${name}[${index}].description`}
                    placeholder=''
                    component={Input}
                    value={name[index] ? name[index].description : ''}
                    inputClassName='flex-2'
                  />
                  <Field
                    name={`${name}[${index}].netAmount`}
                    placeholder=''
                    component={Input}
                    value={name[index] ? name[index].netAmount : ''}
                    inputClassName='flex-1'
                  />
                  <RepeatableAction
                    fieldValues={invoiceItems}
                    arrayHelpers={arrayHelpers}
                    index={index}
                    initialObject={{ description: '', netAmount: '' }}
                  />
                </div>
              ))}
            </div>
          )}
        />
      </div>
    </div>
  )
}

InvoiceItems.propTypes = {
  form: PropTypes.object.isRequired,
  label: PropTypes.string,
  name: PropTypes.string
}

export default inject(
  'invoiceStore',
  'staticData',
  'modalStore',
  'growlStore',
  'deliverableStore'
)(observer(InvoiceItemsForm))

function initializeForm ({ invoice }) {
  let invoiceItems

  if (invoice.id && invoice.invoiceItems.length) {
    invoiceItems = toJS(invoice.invoiceItems)
  } else {
    invoiceItems = [{ description: '', netAmount: null }]
  }

  return {
    invoiceItems: invoiceItems
  }
}

function getValidationSchema () {
  return yup.object().shape({
    invoiceItems: yup.array().of(
      yup.object().shape({
        description: yup
          .string()
          .required('Description is required')
          .typeError('Description is required'),
        netAmount: moneyValidate()
      })
    )
  })
}
